import type {
  AdType,
  CreateProductSetForBudgetsPurchaseForHirerMutation,
  CreateProductSetForBudgetsPurchaseForVisitorMutation,
  DisplayRecommendation,
  TaxCode,
} from '../../generated/graphql-types';
import { isAnz } from '../../utils/isAnz';

interface AdRange {
  minimum: number;
  maximum: number;
}

export interface BudgetOption {
  adPostingFrequency: 'Occasional' | 'Regular' | 'Frequent';
  adRange: AdRange;
  budgetId: string;
  budgetName: string;
  budgetType: 'FlexibleBudget' | 'BrandedBudget';
  budgetValue: number;
  currency: string;
  discounts?: Array<FixedAmountDiscount | PercentageDiscount>;
  duration: {
    value: number;
    unit: 'month';
  };
  productId: string;
  purchasableProducts: string[] | string;
  tax?: {
    code: TaxCode;
    displayRecommendation: DisplayRecommendation;
    value: number;
  };
}

interface BaseDiscount {
  type: 'FixedAmount' | 'Percentage';
  adType: 'Classic' | 'Basic' | 'StandOut' | 'Branded' | 'Premium';
}

type FixedAmountDiscount = BaseDiscount & {
  type: 'FixedAmount';
  value: number;
};

type PercentageDiscount = BaseDiscount & {
  type: 'Percentage';
  value: number;
};

type CreateProductSetForBudgetsPurchase =
  | CreateProductSetForBudgetsPurchaseForVisitorMutation['createProductSetForBudgetsPurchaseForVisitor']
  | CreateProductSetForBudgetsPurchaseForHirerMutation['createProductSetForBudgetsPurchaseForHirer'];

type AdBudgetItem =
  CreateProductSetForBudgetsPurchase['productSet']['items'][number];

type AdBudgetProductItem = Extract<
  AdBudgetItem,
  { __typename: 'AdBudgetProduct' }
>;

type AdBudgetProductItemPrice = Extract<
  AdBudgetProductItem['price'],
  { __typename: 'Price' }
>;
export type AdBudgetProductItemType = Omit<AdBudgetProductItem, 'price'> & {
  price: AdBudgetProductItemPrice;
};

const getDisplayAdType = (
  adType: AdType,
  country: string,
): 'Classic' | 'Basic' | 'StandOut' | 'Branded' | 'Premium' => {
  if (adType === 'CLASSIC' && isAnz(country)) {
    return 'Classic';
  }
  if (adType === 'CLASSIC') {
    return 'Basic';
  }
  if (
    (adType === 'BRANDED_UPGRADE' || adType === 'BRANDED') &&
    isAnz(country)
  ) {
    return 'StandOut';
  }
  if (adType === 'BRANDED_UPGRADE' || adType === 'BRANDED') {
    return 'Branded';
  }
  if (adType === 'PREMIUM') {
    return 'Premium';
  }
  throw new Error('Invalid adType');
};

// TODO move this to backend
const mapBudgetOption = (
  adBudgetProduct: AdBudgetProductItemType,
  country: string,
): BudgetOption => {
  const {
    adPostingFrequency,
    adRange,
    purchasableProducts,
    id: productId,
  } = adBudgetProduct;

  if (
    adPostingFrequency !== 'Occasional' &&
    adPostingFrequency !== 'Regular' &&
    adPostingFrequency !== 'Frequent'
  ) {
    throw new Error();
  }

  const durationFeature = adBudgetProduct.features.flatMap((feature) =>
    feature?.__typename === 'DurationFeature' ? [feature] : [],
  )[0];

  if (durationFeature === undefined) {
    throw new Error('no duration feature');
  }

  // This determines if it is a Branded budget
  const isBrandedFeaturesFeaturePresent = Boolean(
    adBudgetProduct.features.find(
      (feature) => feature?.__typename === 'BrandedFeaturesFeature',
    ),
  );

  const discounts = adBudgetProduct.features
    .flatMap((feature) =>
      feature?.__typename === 'AdTypeDiscountsFeature'
        ? [
            feature.discounts.flatMap(
              (
                discountFeature,
              ): Array<FixedAmountDiscount | PercentageDiscount> => {
                if (
                  discountFeature.discount.discountPercentage > 0 &&
                  discountFeature.discount.discountAmount.value > 0
                ) {
                  throw new Error(
                    'fixed and percentage discounts specified in single feature',
                  );
                }
                if (discountFeature.discount.discountPercentage > 0) {
                  return [
                    {
                      type: 'Percentage',
                      adType: getDisplayAdType(discountFeature.adType, country),
                      value: discountFeature.discount.discountPercentage,
                    },
                  ];
                }
                if (discountFeature.discount.discountAmount.value > 0) {
                  return [
                    {
                      type: 'FixedAmount',
                      adType: getDisplayAdType(discountFeature.adType, country),
                      value: discountFeature.discount.discountAmount.value,
                    },
                  ];
                }
                return [];
              },
            ),
          ]
        : [],
    )
    .flat();

  const hasFixedAmountDiscounts = discounts.some(
    (discount) => discount.type === 'FixedAmount',
  );

  let discountsToDisplay = discounts;

  /* This removes Classic/Basic ad discount for Branded budgets if branded ad component is fixed compared to classic component
   * Example for AU/NZ (has Classic ads discount)
   * 15% off Classic Ads
   * $5 off StandOut features
   *
   * Example for TH (No Basic ads discount)
   * 37% off Basic ads <-- this is removed
   * 37% off Branded ads
   * 10% off Premium ads
   */
  if (isBrandedFeaturesFeaturePresent && !hasFixedAmountDiscounts) {
    discountsToDisplay = discounts.filter(
      (discount) =>
        discount.adType !== 'Classic' && discount.adType !== 'Basic',
    );
  }

  if (!purchasableProducts) {
    throw new Error('no purchasable products');
  }

  let productToDisplay: string | string[] = '';
  if (
    purchasableProducts.__typename === 'PurchasableProductsList' &&
    purchasableProducts.products
  ) {
    productToDisplay = purchasableProducts.products;
  }
  if (
    purchasableProducts.__typename === 'PurchasableProductsString' &&
    purchasableProducts.product
  ) {
    productToDisplay = purchasableProducts.product;
  }

  const durationUnit = durationFeature.unit.toLowerCase();

  if (durationUnit !== 'month') {
    throw new Error(`unsupported duration unit: ${durationFeature.unit}`);
  }

  const code = adBudgetProduct.price.tax?.code ?? undefined;
  const displayRecommendation =
    adBudgetProduct.price.tax?.displayRecommendation ?? undefined;
  const value = adBudgetProduct.price.tax?.amount?.value ?? undefined;

  return {
    budgetValue: adBudgetProduct.price.netPrice.value,
    currency: adBudgetProduct.price.netPrice.currency,
    tax:
      code && displayRecommendation && value
        ? {
            code,
            value,
            displayRecommendation,
          }
        : undefined,
    adPostingFrequency,
    budgetId: adBudgetProduct.id,
    budgetName: adBudgetProduct.name,
    budgetType: isBrandedFeaturesFeaturePresent
      ? 'BrandedBudget'
      : 'FlexibleBudget',
    discounts: discountsToDisplay,
    duration: {
      value: durationFeature.value,
      unit: durationUnit,
    },
    adRange,
    productId,
    purchasableProducts: productToDisplay,
  };
};

export { mapBudgetOption };
